import { REACT_APP_API_URL } from '../const';
import { ChatRequest, NavigationRequest, FunctionCallRequest } from '../models/types';

class ApiService {
  constructor() {
    this.baseUrl = REACT_APP_API_URL;
  }

  // Generic request method
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const defaultOptions = {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };

    const requestOptions = { ...defaultOptions, ...options };

    try {
      console.log(`📡 Making request to: ${url}`);
      console.log('📦 Request options:', {
        method: requestOptions.method,
        headers: requestOptions.headers,
        body: requestOptions.body ? JSON.parse(requestOptions.body) : undefined
      });
      
      const response = await fetch(url, requestOptions);
      console.log(`📥 Response status: ${response.status}`);
      
      const responseText = await response.text();
      console.log('📥 Raw response:', responseText);
      
      if (!response.ok) {
        console.error('❌ Response error:', {
          status: response.status,
          statusText: response.statusText,
          body: responseText
        });
        throw new Error(`HTTP error! status: ${response.status}, text: ${responseText}`);
      }

      try {
        const jsonResponse = JSON.parse(responseText);
        console.log('✅ Parsed response:', jsonResponse);
        return jsonResponse;
      } catch (parseError) {
        console.error('❌ Failed to parse response as JSON:', {
          error: parseError,
          text: responseText
        });
        throw new Error('Invalid JSON response from server');
      }
    } catch (error) {
      console.error(`❌ API request failed for ${endpoint}:`, {
        error: error.message,
        stack: error.stack,
        url,
        options: requestOptions
      });
      throw error;
    }
  }

  // Chat API
  async sendChatMessage(message, conversationId = null, conversationHistory = [], spatialContext = null) {
    const chatRequest = new ChatRequest(message, conversationId, conversationHistory, spatialContext);
    
    return this.makeRequest('/chat', {
      method: 'POST',
      body: JSON.stringify(chatRequest)
    });
  }

  // Project configuration
  async getProjectConfig() {
    return this.makeRequest('/project-config', {
      method: 'GET'
    });
  }

  // Matterport APIs
  async updateMatterportTags(tags) {
    return this.makeRequest('/matterport/update-tags', {
      method: 'POST',
      body: JSON.stringify(tags)
    });
  }

  async updateSpatialContext(spatialData) {
    return this.makeRequest('/matterport/update-spatial-context', {
      method: 'POST',
      body: JSON.stringify(spatialData)
    });
  }

  async getMatterportTags() {
    return this.makeRequest('/matterport/tags', {
      method: 'GET'
    });
  }

  async getSpatialContext() {
    return this.makeRequest('/matterport/spatial-context', {
      method: 'GET'
    });
  }

  async navigateToLocation(targetLabel, conversationId = null) {
    const navigationRequest = new NavigationRequest(targetLabel, conversationId);
    
    return this.makeRequest('/matterport/navigate', {
      method: 'POST',
      body: JSON.stringify(navigationRequest)
    });
  }

  async executeFunctionCall(functionName, parameters = {}) {
    const functionCallRequest = new FunctionCallRequest(functionName, parameters);
    
    return this.makeRequest('/function-call', {
      method: 'POST',
      body: JSON.stringify(functionCallRequest)
    });
  }

  // Health check
  async healthCheck() {
    return this.makeRequest('/health', {
      method: 'GET'
    });
  }

  // Debug info
  async getDebugInfo() {
    return this.makeRequest('/debug', {
      method: 'GET'
    });
  }
}

// Create singleton instance
const apiService = new ApiService();
export default apiService; 